import { useState } from "react";
import { Wand2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCreateAgentTemplate,
  useGenerateAgentTemplate,
} from "@/features/agents/hooks";
import type { AgentTemplate } from "@/features/agents/api";

const VERTICAL_OPTIONS = [
  { value: "healthcare", label: "Healthcare" },
  { value: "hospitality", label: "Hospitality" },
  { value: "real_estate", label: "Real Estate" },
  { value: "legal", label: "Legal" },
  { value: "retail", label: "Retail" },
  { value: "finance", label: "Finance" },
  { value: "education", label: "Education" },
  { value: "home_services", label: "Home Services" },
  { value: "automotive", label: "Automotive" },
  { value: "other", label: "Other" },
];

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (tpl: AgentTemplate) => void;
};

export default function CreateTemplateDialog({
  open,
  onOpenChange,
  onCreated,
}: Props) {
  const create = useCreateAgentTemplate();
  const generate = useGenerateAgentTemplate();

  const [name, setName] = useState("");
  const [vertical, setVertical] = useState("other");
  const [tone, setTone] = useState<"friendly" | "professional" | "casual">(
    "friendly",
  );
  const [language, setLanguage] = useState("en-US");
  const [voiceProvider, setVoiceProvider] = useState("elevenlabs");
  const [businessDescription, setBusinessDescription] = useState("");
  const [useCase, setUseCase] = useState("");
  const [description, setDescription] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [initialMessage, setInitialMessage] = useState("");

  function reset() {
    setName("");
    setVertical("other");
    setTone("friendly");
    setLanguage("en-US");
    setVoiceProvider("elevenlabs");
    setBusinessDescription("");
    setUseCase("");
    setDescription("");
    setSystemPrompt("");
    setInitialMessage("");
  }

  async function onGenerate() {
    if (!businessDescription.trim() || !useCase.trim()) {
      toast.error(
        "Fill in the business description and what the agent should do first.",
      );
      return;
    }
    try {
      const out = await generate.mutateAsync({
        business_description: businessDescription,
        use_case: useCase,
        vertical,
        tone,
        language,
      });
      if (!name.trim() && out.name) setName(out.name);
      setDescription(out.description);
      setSystemPrompt(out.system_prompt);
      setInitialMessage(out.initial_message);
      toast.success("Generated. Review and edit before saving.");
    } catch (e) {
      const err = e as {
        response?: { data?: { error?: string } };
      };
      toast.error(
        err?.response?.data?.error || "Failed to generate template",
      );
    }
  }

  async function onSave() {
    if (!name.trim()) {
      toast.error("Give the template a name.");
      return;
    }
    if (!systemPrompt.trim()) {
      toast.error(
        "System prompt is empty — generate it with AI or write your own.",
      );
      return;
    }
    try {
      const created = await create.mutateAsync({
        name: name.trim(),
        vertical,
        description,
        default_system_prompt: systemPrompt,
        default_initial_message: initialMessage,
        default_voice_provider: voiceProvider,
        default_language: language,
      });
      toast.success("Template created");
      onCreated?.(created);
      reset();
      onOpenChange(false);
    } catch (e) {
      const err = e as {
        response?: { data?: { detail?: string; name?: string[] } };
      };
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.name?.[0] ||
          "Failed to create template",
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create your own template</DialogTitle>
          <DialogDescription>
            Describe your business and what the agent should do. Then click
            <span className="font-medium"> Generate with AI </span>
            to draft the system prompt — or write it yourself.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="t-name">Template name</Label>
              <Input
                id="t-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Auto Repair Front Desk"
                maxLength={120}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Vertical</Label>
              <Select value={vertical} onValueChange={setVertical}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VERTICAL_OPTIONS.map((v) => (
                    <SelectItem key={v.value} value={v.value}>
                      {v.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="biz">Business description</Label>
            <Textarea
              id="biz"
              rows={2}
              value={businessDescription}
              onChange={(e) => setBusinessDescription(e.target.value)}
              placeholder="e.g., Small auto repair shop in Chicago, serving 20-30 customers a day."
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="uc">What should the agent do?</Label>
            <Textarea
              id="uc"
              rows={3}
              value={useCase}
              onChange={(e) => setUseCase(e.target.value)}
              placeholder="e.g., Book service appointments, answer questions about hours and pricing for common services, escalate to the manager for complaints."
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Tone</Label>
              <Select
                value={tone}
                onValueChange={(v) =>
                  setTone(v as "friendly" | "professional" | "casual")
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="friendly">Friendly</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="casual">Casual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en-US">English (US)</SelectItem>
                  <SelectItem value="es-ES">Spanish</SelectItem>
                  <SelectItem value="fr-FR">French</SelectItem>
                  <SelectItem value="hi-IN">Hindi</SelectItem>
                  <SelectItem value="ar-SA">Arabic</SelectItem>
                  <SelectItem value="de-DE">German</SelectItem>
                  <SelectItem value="pt-BR">Portuguese (BR)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Voice provider</Label>
              <Select value={voiceProvider} onValueChange={setVoiceProvider}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
                  <SelectItem value="cartesia">Cartesia</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onGenerate}
              disabled={generate.isPending}
            >
              <Wand2 className="size-4" />
              {generate.isPending ? "Generating…" : "Generate with AI"}
            </Button>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="t-desc">Short description (1 line)</Label>
            <Input
              id="t-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="One-sentence summary shown on the card."
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sp">System prompt</Label>
            <Textarea
              id="sp"
              rows={10}
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              placeholder="Will be filled in when you click Generate with AI — or write it yourself."
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="im">Initial message</Label>
            <Textarea
              id="im"
              rows={2}
              value={initialMessage}
              onChange={(e) => setInitialMessage(e.target.value)}
              placeholder="Will be filled in when you click Generate with AI — or write it yourself."
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button onClick={onSave} disabled={create.isPending}>
            {create.isPending ? "Saving…" : "Save template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
